import * as XLSX from 'xlsx'
import type {
  EstadoCuenta,
  Genero,
  ImportRow,
  ImportWarning,
  ParseResult,
  TipoServicio,
} from './types'

// -----------------------------------------------------------------------------
// Aliases de columnas: el core a veces cambia mayusculas/acentos/sinonimos.
// Buscamos case-insensitive y trim, primera coincidencia gana.
// -----------------------------------------------------------------------------
const COLUMN_ALIASES: Record<string, string[]> = {
  no_asociado: ['NO. ASOCIADO', 'No. Asociado', 'NUMERO DE ASOCIADO', 'NUMERO ASOCIADO'],
  cuenta_id: ['Cuenta', 'CUENTA', 'No. Cuenta', 'NUMERO CUENTA'],
  pin_core: ['Pin', 'PIN'],
  tipo_servicio: ['Tipo de Servicio', 'Tipo Servicio', 'TIPO SERVICIO', 'TIPO DE SERVICIO'],
  producto_codigo: ['Producto', 'PRODUCTO', 'Codigo Producto'],
  producto_nombre: ['Descripcion Producto', 'Producto Nombre', 'DESCRIPCION PRODUCTO'],
  apertura: ['Apertura', 'APERTURA', 'Fecha Apertura', 'FECHA APERTURA'],
  cierre: ['Cierre', 'Fecha Cierre', 'CIERRE', 'FECHA CIERRE'],
  saldo_disponible: ['Saldo Disponible', 'SALDO DISPONIBLE', 'Disponible'],
  saldo_pignorado: ['Saldo Pignorado', 'SALDO PIGNORADO', 'Pignorado'],
  saldo_total: ['Saldo Total', 'SALDO TOTAL', 'Total'],
  activa: ['Activa', 'ACTIVA'],
  cerrado: ['Cerrado', 'CERRADO'],
  cierre_anticipado: ['Cierre Anticipado', 'CIERRE ANTICIPADO'],
  dpi: ['DPI', 'Dpi', 'dpi'],
  profesion: ['Profesion/Oficio', 'Profesion', 'Oficio', 'PROFESION', 'PROFESION/OFICIO'],
  direccion: ['Direccion', 'Dirección', 'DIRECCION'],
  municipio: ['Municipio', 'MUNICIPIO'],
  departamento: ['Departamento', 'DEPARTAMENTO'],
  telefono: ['Telefono', 'Teléfono', 'TELEFONO'],
  usuario_core: ['Usuario', 'USUARIO', 'Usuario Core'],
  actividad: ['Actividad Economica', 'Actividad', 'ACTIVIDAD ECONOMICA'],
  nombre: ['ASOCIADO', 'Asociado', 'Nombre Completo', 'Nombre', 'NOMBRE', 'NOMBRE COMPLETO'],
  genero: ['Genero', 'Género', 'GENERO'],
  // En el export del core, 'Sucursal' viene combinada como '001 - NEOCOOP CORE'
  // y 'Region' como '001 - CENTRAL'. El parser las splittea por ' - '.
  sucursal: ['Sucursal', 'SUCURSAL'],
  region: ['Region', 'REGION'],
  vencimiento: ['Vencimiento/Intereses', 'Vencimiento', 'Fecha Vencimiento', 'VENCIMIENTO', 'FECHA VENCIMIENTO'],
  ultimo_movimiento: ['Ultimo Movimiento', 'Ultimo Mov', 'ULTIMO MOVIMIENTO', 'ULTIMO MOV'],
}

// Mapeo tipo_servicio del xlsx → enum de DB. El core a veces abrevia.
const TIPO_SERVICIO_MAP: Record<string, TipoServicio> = {
  'APORTACIONES ORDINARIAS': 'APORTACIONES ORDINARIAS',
  'APORTACIONES ORDINARIA': 'APORTACIONES ORDINARIAS',
  'APORTACIONES': 'APORTACIONES ORDINARIAS',
  'AHORRO CORRIENTE': 'AHORRO CORRIENTE',
  'AHORRO': 'AHORRO CORRIENTE',
  'CUENTA DE AHORRO': 'AHORRO CORRIENTE',
  'AHORRO PLAZO FIJO': 'AHORRO PLAZO FIJO',
  'PLAZO FIJO': 'AHORRO PLAZO FIJO',
  'DEPOSITO A PLAZO': 'AHORRO PLAZO FIJO',
  'CREDITO': 'CREDITO',
  'APORTACIONES EXTRAORDINARIAS': 'APORTACIONES EXTRAORDINARIAS',
}

// -----------------------------------------------------------------------------
// Helpers de normalizacion
// -----------------------------------------------------------------------------

function normalizeKey(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, ' ')
}

/** Dado un row (object con keys originales del xlsx), busca por aliases. */
function pick(row: Record<string, unknown>, aliases: string[]): unknown {
  const normalized: Record<string, unknown> = {}
  for (const k of Object.keys(row)) normalized[normalizeKey(k)] = row[k]
  for (const alias of aliases) {
    const v = normalized[normalizeKey(alias)]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return null
}

function asString(v: unknown): string | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') {
    // Ojo: Excel a veces entrega numeros donde queremos string (DPI, cuenta).
    // Numero seguro hasta 2^53, convertimos sin perdida si es entero.
    if (Number.isInteger(v)) return v.toString()
    return v.toString()
  }
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'nan') return null
  return s
}

function asNumber(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0
  if (typeof v === 'number') return v
  const s = String(v).replace(/,/g, '').trim()
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : 0
}

function asBoolSI(v: unknown): boolean {
  const s = asString(v)
  if (!s) return false
  return s.toUpperCase() === 'SI' || s === '1' || s.toUpperCase() === 'TRUE'
}

function asGenero(v: unknown): Genero {
  const s = asString(v)
  if (!s) return 'MASCULINO'
  const up = s.toUpperCase()
  if (up.startsWith('F')) return 'FEMENINO'
  return 'MASCULINO'
}

/**
 * Parsea fechas del core tolerando sus variantes:
 *   '19/02/2026 03:54 PM'   → 12h con AM/PM
 *   '31/12/2025 23:16 PM'   → 24h con AM/PM espurio (la hora manda)
 *   '19/02/2026 15:54'      → 24h sin sufijo
 *   '19/02/2026'            → solo fecha
 *   '2026-02-19 15:54:00'   → ISO
 *   Date (si SheetJS lo entrega como Date)
 */
/**
 * Convierte un serial de Excel a Date UTC. Excel guarda fechas como "dias
 * desde epoch". El epoch es 30/12/1899 (compensando el bug de Excel que trata
 * 1900 como bisiesto). Esta interpretacion coincide con pandas y con el seed
 * Python, evitando desajustes entre la ruta TS y la SQL.
 */
function excelSerialToISO(serial: number): string {
  const epoch = Date.UTC(1899, 11, 30) // 1899-12-30 UTC
  const ms = epoch + serial * 86400000
  const d = new Date(ms)
  // El Date es UTC-midnight del dia. Lo "reinterpretamos" como medianoche
  // Guatemala (-06:00) para alinear con el convention del seed Python.
  const y = d.getUTCFullYear()
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  const h = String(d.getUTCHours()).padStart(2, '0')
  const mi = String(d.getUTCMinutes()).padStart(2, '0')
  return new Date(`${y}-${mo}-${day}T${h}:${mi}:00-06:00`).toISOString()
}

function parseFechaGT(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  // Si el parser configurado con cellDates:false entrega serial numerico
  if (typeof v === 'number' && Number.isFinite(v)) {
    return excelSerialToISO(v)
  }
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return null
    // Fallback (solo si cellDates:true en algun punto): reconstruir desde
    // componentes UTC para no depender de la tz del browser.
    const y = v.getUTCFullYear()
    const mo = String(v.getUTCMonth() + 1).padStart(2, '0')
    const d = String(v.getUTCDate()).padStart(2, '0')
    return new Date(`${y}-${mo}-${d}T00:00:00-06:00`).toISOString()
  }
  let s = String(v).trim()
  if (!s || s.toLowerCase() === 'nan') return null

  // Si viene '31/12/2025 23:16 PM' (hora >= 13 con AM/PM), quitamos el sufijo.
  const match = s.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2})(?:\s*(AM|PM|am|pm))?\s*$/)
  if (match) {
    const [, d, mo, y, h, mi] = match
    const hour = parseInt(h, 10)
    const minute = parseInt(mi, 10)
    let realHour = hour
    // Si tiene sufijo AM/PM y la hora es 1-12, aplicar conversion normal
    const suffix = match[6]?.toUpperCase()
    if (suffix && hour >= 1 && hour <= 12) {
      if (suffix === 'PM' && hour !== 12) realHour = hour + 12
      if (suffix === 'AM' && hour === 12) realHour = 0
    }
    // Si hora >= 13, ignorar AM/PM (formato roto del core)
    const iso = `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}T${String(realHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00-06:00`
    const dt = new Date(iso)
    if (!isNaN(dt.getTime())) return dt.toISOString()
  }

  // Intentar dd/mm/yyyy sin hora — ANTES del fallback ISO porque new Date(s)
  // interpreta "11/02/2027" como MM/DD/YYYY (US) y rompe las fechas GT.
  const onlyDate = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (onlyDate) {
    const [, d, mo, y] = onlyDate
    const dt = new Date(`${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}T00:00:00-06:00`)
    if (!isNaN(dt.getTime())) return dt.toISOString()
  }

  // ISO o similares: solo aceptar si parece ISO (YYYY-MM-DD...). Evita que
  // new Date(s) aplique heuristicas de locale ambiguas.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const iso = new Date(s)
    if (!isNaN(iso.getTime())) return iso.toISOString()
  }

  return null
}

function normalizePhoneGT(raw: unknown): string | null {
  const s = asString(raw)
  if (!s) return null
  const digits = s.replace(/\D/g, '')
  if (digits.length === 8) return `+502${digits}`
  if (digits.length === 11 && digits.startsWith('502')) return `+${digits}`
  return s
}

function normalizeTipoServicio(v: unknown): TipoServicio | null {
  const s = asString(v)
  if (!s) return null
  const up = s.toUpperCase().trim()
  return TIPO_SERVICIO_MAP[up] ?? null
}

function deriveEstado(row: Record<string, unknown>): EstadoCuenta {
  // Regla v0.1 del docs/core-export-schema.md
  const cerrado = asString(pick(row, COLUMN_ALIASES.cerrado))?.toUpperCase() === 'SI'
  const cierreFecha = parseFechaGT(pick(row, COLUMN_ALIASES.cierre))
  const anticipado = asString(pick(row, COLUMN_ALIASES.cierre_anticipado))?.toUpperCase() === 'SI'
  const activa = asString(pick(row, COLUMN_ALIASES.activa))?.toUpperCase()

  if (cerrado || cierreFecha) return 'cerrada'
  if (anticipado) return 'cerrada'
  if (activa === 'NO') return 'cerrada'
  return 'activa'
}

function padCuentaId(raw: string): string {
  // El core a veces manda '1-1-1' o '001-001-1' — normalizamos a 3-3-6.
  const parts = raw.split('-').map((p) => p.trim())
  if (parts.length !== 3) return raw
  return `${parts[0].padStart(3, '0')}-${parts[1].padStart(3, '0')}-${parts[2].padStart(6, '0')}`
}

function padNoAsociado(raw: string): string {
  // '2025-1' → '2025-000001'. Respetamos el ano.
  const parts = raw.split('-').map((p) => p.trim())
  if (parts.length !== 2) return raw
  return `${parts[0]}-${parts[1].padStart(6, '0')}`
}

// -----------------------------------------------------------------------------
// SHA-256 del archivo en el browser
// -----------------------------------------------------------------------------
export async function computeSha256(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const hashBuf = await crypto.subtle.digest('SHA-256', buf)
  const bytes = new Uint8Array(hashBuf)
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// -----------------------------------------------------------------------------
// Parser principal
// -----------------------------------------------------------------------------
export async function parseXlsx(file: File): Promise<ParseResult> {
  const fileHash = await computeSha256(file)
  const buf = await file.arrayBuffer()
  // cellDates: false → las celdas de fecha nativa se quedan como numero
  // serial. Las convertimos nosotros en parseFechaGT con el mismo epoch
  // que usa pandas (1900) para coincidir con el seed Python.
  const wb = XLSX.read(buf, { type: 'array', cellDates: false })
  const ws = wb.Sheets[wb.SheetNames[0]]
  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
    defval: null,
    raw: true, // preservar tipos nativos (fechas como Date, numeros como number)
  })

  const warnings: ImportWarning[] = []
  const rows: ImportRow[] = []

  rawRows.forEach((raw, idx) => {
    try {
      // Campos clave obligatorios
      const noAsoRaw = asString(pick(raw, COLUMN_ALIASES.no_asociado))
      const cuentaRaw = asString(pick(raw, COLUMN_ALIASES.cuenta_id))
      const dpi = asString(pick(raw, COLUMN_ALIASES.dpi))
      const nombre = asString(pick(raw, COLUMN_ALIASES.nombre))
      const tipo = normalizeTipoServicio(pick(raw, COLUMN_ALIASES.tipo_servicio))

      if (!noAsoRaw) {
        warnings.push({ rowIndex: idx, field: 'no_asociado', message: 'Fila sin NO. ASOCIADO, se omite' })
        return
      }
      if (!cuentaRaw) {
        warnings.push({ rowIndex: idx, field: 'cuenta_id', message: 'Fila sin Cuenta, se omite' })
        return
      }
      if (!dpi) {
        warnings.push({ rowIndex: idx, field: 'dpi', message: `Asociado ${noAsoRaw} sin DPI, se omite` })
        return
      }
      if (!nombre) {
        warnings.push({ rowIndex: idx, field: 'nombre', message: `Asociado ${noAsoRaw} sin nombre, se omite` })
        return
      }
      if (!tipo) {
        warnings.push({
          rowIndex: idx,
          field: 'tipo_servicio',
          message: `Cuenta ${cuentaRaw} con tipo_servicio no reconocido: ${pick(raw, COLUMN_ALIASES.tipo_servicio)}`,
        })
        return
      }

      // DPI debe ser 13 digitos string (preservar ceros)
      const dpiClean = dpi.replace(/\D/g, '')
      if (dpiClean.length !== 13) {
        warnings.push({
          rowIndex: idx,
          field: 'dpi',
          message: `DPI con longitud invalida (${dpiClean.length}) en asociado ${noAsoRaw}`,
          rawValue: dpi,
        })
      }

      const fechaApertura = parseFechaGT(pick(raw, COLUMN_ALIASES.apertura))
      if (!fechaApertura) {
        warnings.push({
          rowIndex: idx,
          field: 'fecha_apertura',
          message: `Cuenta ${cuentaRaw} sin fecha de apertura valida, usando hoy`,
        })
      }

      // Sucursal/Region en el xlsx vienen combinadas: '001 - NEOCOOP CORE'.
      // Splitteamos por ' - ' para quedarnos con code y name.
      const splitPair = (raw: string | null): { code: string | null; name: string | null } => {
        if (!raw) return { code: null, name: null }
        const parts = raw.split(' - ')
        if (parts.length >= 2) {
          return { code: parts[0].trim(), name: parts.slice(1).join(' - ').trim() }
        }
        return { code: raw.trim(), name: null }
      }
      const sucursal = splitPair(asString(pick(raw, COLUMN_ALIASES.sucursal)))
      const region = splitPair(asString(pick(raw, COLUMN_ALIASES.region)))
      const sucursalCode = sucursal.code ?? '001'

      const row: ImportRow = {
        no_asociado: padNoAsociado(noAsoRaw),
        dpi: dpiClean,
        nombre_completo: nombre,
        genero: asGenero(pick(raw, COLUMN_ALIASES.genero)),
        profesion: asString(pick(raw, COLUMN_ALIASES.profesion)),
        direccion: asString(pick(raw, COLUMN_ALIASES.direccion)),
        municipio: asString(pick(raw, COLUMN_ALIASES.municipio)),
        departamento: asString(pick(raw, COLUMN_ALIASES.departamento)),
        telefono_e164: normalizePhoneGT(pick(raw, COLUMN_ALIASES.telefono)),
        usuario_core: asString(pick(raw, COLUMN_ALIASES.usuario_core)) ?? 'UNKNOWN',
        actividad_economica: asString(pick(raw, COLUMN_ALIASES.actividad)),

        branch_code: sucursalCode,
        branch_name: sucursal.name,
        region_code: region.code,
        region_name: region.name,

        cuenta_id: padCuentaId(cuentaRaw),
        tipo_servicio: tipo,
        pin_core: asString(pick(raw, COLUMN_ALIASES.pin_core)),
        fecha_apertura: fechaApertura ?? new Date().toISOString(),
        ultimo_movimiento: parseFechaGT(pick(raw, COLUMN_ALIASES.ultimo_movimiento)),
        fecha_vencimiento: parseFechaGT(pick(raw, COLUMN_ALIASES.vencimiento)),
        fecha_cierre: parseFechaGT(pick(raw, COLUMN_ALIASES.cierre)),
        cierre_anticipado: asBoolSI(pick(raw, COLUMN_ALIASES.cierre_anticipado)),
        saldo_disponible: asNumber(pick(raw, COLUMN_ALIASES.saldo_disponible)),
        saldo_pignorado: asNumber(pick(raw, COLUMN_ALIASES.saldo_pignorado)),
        saldo_total: asNumber(pick(raw, COLUMN_ALIASES.saldo_total)),
        estado: deriveEstado(raw),
      }

      // Validacion extra: saldo_total = disponible + pignorado (tolerancia 0.01)
      const sum = row.saldo_disponible + row.saldo_pignorado
      if (Math.abs(sum - row.saldo_total) > 0.01) {
        warnings.push({
          rowIndex: idx,
          field: 'saldo_total',
          message: `Cuenta ${row.cuenta_id}: saldo_total (${row.saldo_total}) != disp + pig (${sum.toFixed(2)})`,
        })
      }

      rows.push(row)
    } catch (e: unknown) {
      warnings.push({
        rowIndex: idx,
        field: '_exception',
        message: e instanceof Error ? e.message : 'Error desconocido parseando fila',
      })
    }
  })

  const asociados = new Set(rows.map((r) => r.no_asociado))
  const cuentas = new Set(rows.map((r) => r.cuenta_id))

  return {
    fileName: file.name,
    fileHash,
    fileSize: file.size,
    rows,
    warnings,
    asociadosUnique: asociados.size,
    cuentasUnique: cuentas.size,
  }
}
